import React from "react";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import { FiEdit2, FiTrash2, FiEye } from "react-icons/fi";

export type AirportTableFilters = {
  airportCode?: string;
  airportName?: string;
  cityCode?: string;
  countryCode?: string;
};

export type AirportRow = {
  id: string | number;
  airportName: string;
  airportCode: string;
  cityName?: string | null;
  cityCode?: string | null;
  countryName?: string | null;
  countryCode?: string | null;
};

export default function AirportsTable({
  pageRows,
  serialStart,
  filters,
  setFilters,
  onView,
  onEdit,
  onDelete,
}: {
  pageRows: AirportRow[];
  serialStart: number;
  filters: AirportTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<AirportTableFilters>>;
  onView: (r: AirportRow) => void;
  onEdit: (r: AirportRow) => void;
  onDelete: (r: AirportRow) => void;
}) {
  return (
    // Square bottom: the pagination bar below completes this card.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      <div className="hidden overflow-x-auto overflow-visible md:block">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>
              <Th>Airport Name</Th>
              <Th>
                <div className="flex items-center gap-1.5"><span>Code</span><ColumnFilter label="Airport Code"><Input placeholder="Contains..." value={filters.airportCode || ""} onChange={(e) => setFilters((f) => ({ ...f, airportCode: e.target.value }))} /></ColumnFilter></div>
              </Th>
              <Th>City</Th>
              <Th>
                <div className="flex items-center gap-1.5"><span>City Code</span><ColumnFilter label="City Code"><Input placeholder="Contains..." value={filters.cityCode || ""} onChange={(e) => setFilters((f) => ({ ...f, cityCode: e.target.value }))} /></ColumnFilter></div>
              </Th>
              <Th>Country</Th>
              <Th>
                <div className="flex items-center gap-1.5"><span>Country Code</span><ColumnFilter label="Country Code"><Input placeholder="Contains..." value={filters.countryCode || ""} onChange={(e) => setFilters((f) => ({ ...f, countryCode: e.target.value }))} /></ColumnFilter></div>
              </Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-10 text-center text-sm text-gray-500">No airports found.</td>
              </tr>
            ) : (
              pageRows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <Td>{serialStart + i + 1}</Td>
                  <Td className="text-gray-900">{r.airportName}</Td>
                  <Td className="font-medium text-gray-900">{r.airportCode}</Td>
                  <Td className="text-gray-700">{r.cityName || <span className="text-gray-400">—</span>}</Td>
                  <Td className="text-gray-700">{r.cityCode || <span className="text-gray-400">—</span>}</Td>
                  <Td className="text-gray-700">{r.countryName || <span className="text-gray-400">—</span>}</Td>
                  <Td className="text-gray-700">{r.countryCode || <span className="text-gray-400">—</span>}</Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onView(r)}><FiEye /></button>
                      <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onEdit(r)}><FiEdit2 /></button>
                      <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50" onClick={() => onDelete(r)}><FiTrash2 /></button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="divide-y divide-gray-200 md:hidden">
        {pageRows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No airports found.</div>
        ) : (
          pageRows.map((r, i) => (
            <div key={r.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="pr-2">
                  <div className="text-sm font-medium text-gray-900">{serialStart + i + 1}. {r.airportCode} — {r.airportName}</div>
                  <div className="mt-0.5 text-xs text-gray-600">{r.cityCode || "—"} • {r.countryCode || "—"}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onView(r)}><FiEye /></button>
                  <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onEdit(r)}><FiEdit2 /></button>
                  <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50" onClick={() => onDelete(r)}><FiTrash2 /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th scope="col" className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}>{children}</th>;
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}
